import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const TABS = ['Meetings', 'Attendances', 'AGM Materials', 'Hotel Registration'];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

function FormSelect({ label, options, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <select {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminAGM() {
  const [tab, setTab] = useState('Meetings');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(() => {
    api.get('/admin/agm/stats/').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">AGM Management</h1>
      <p className="text-sm text-gray-500 mb-6">Manage Annual General Meetings, registrations, and attendance</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total AGMs" value={stats.total} color="text-gray-900" />
          <StatCard label="Upcoming" value={stats.upcoming} color="text-blue-600" />
          <StatCard label="Completed" value={stats.completed} color="text-purple-600" />
          <StatCard label="Registered" value={stats.total_attendances} color="text-green-600" />
        </div>
      )}

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t}</button>
        ))}
      </div>

      {tab === 'Meetings' && <MeetingsTab onRefreshStats={fetchStats} />}
      {tab === 'Attendances' && <AGMAttendancesTab />}
      {tab === 'AGM Materials' && <AGMMaterialsTab />}
      {tab === 'Hotel Registration' && <HotelRegistrationTab />}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ MEETINGS TAB ═══
// ══════════════════════════════════════════════════════════════

function MeetingsTab({ onRefreshStats }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', venue: '', year: new Date().getFullYear().toString(),
    date: '', time_start: '', time_end: '', agenda: '', status: 'draft',
  });
  const [coverImage, setCoverImage] = useState(null);

  const fetchMeetings = useCallback(() => {
    setLoading(true);
    let url = '/admin/agm/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    if (statusFilter) url += `status=${statusFilter}&`;
    api.get(url)
      .then(r => setMeetings(r.data.results || r.data))
      .catch(() => toast.error('Failed to load AGM meetings'))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const resetForm = () => {
    setForm({
      title: '', description: '', venue: '', year: new Date().getFullYear().toString(),
      date: '', time_start: '', time_end: '', agenda: '', status: 'draft',
    });
    setCoverImage(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (m) => {
    setForm({
      title: m.title, description: m.description || '', venue: m.venue || '',
      year: m.year?.toString() || '', date: m.date, time_start: m.time_start || '',
      time_end: m.time_end || '', agenda: m.agenda || '', status: m.status,
    });
    setCoverImage(null);
    setEditing(m);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) fd.append(k, v);
    });
    if (coverImage) fd.append('cover_image', coverImage);

    try {
      if (editing) {
        await api.patch(`/admin/agm/${editing.id}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('AGM meeting updated');
      } else {
        await api.post('/admin/agm/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('AGM meeting created');
      }
      resetForm();
      fetchMeetings();
      onRefreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save AGM meeting');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this AGM meeting?')) return;
    try {
      await api.delete(`/admin/agm/${id}/`);
      toast.success('AGM meeting deleted');
      fetchMeetings();
      onRefreshStats();
    } catch { toast.error('Failed to delete meeting'); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input type="text" placeholder="Search meetings..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canAdd(PERMISSIONS.ADD_AGM) && (
        <button onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition whitespace-nowrap">
          {showForm ? 'Cancel' : '+ New AGM'}
        </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-4">{editing ? 'Edit AGM Meeting' : 'Create New AGM Meeting'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <FormInput label="Title" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <FormInput label="Year" type="number" required value={form.year} onChange={e => setForm({...form, year: e.target.value})} />
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
              <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <FormInput label="Venue" value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} />
            <FormInput label="Date" type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
            <FormSelect label="Status" options={STATUS_OPTIONS} value={form.status} onChange={e => setForm({...form, status: e.target.value})} />
            <FormInput label="Time Start" type="time" value={form.time_start} onChange={e => setForm({...form, time_start: e.target.value})} />
            <FormInput label="Time End" type="time" value={form.time_end} onChange={e => setForm({...form, time_end: e.target.value})} />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Cover Image</label>
              <input type="file" accept="image/*" onChange={e => setCoverImage(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-[#0B5D1E]/10 file:text-[#0B5D1E]" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">Agenda</label>
              <textarea rows={4} value={form.agenda} onChange={e => setForm({...form, agenda: e.target.value})}
                placeholder="Meeting agenda items..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button type="submit" className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
              {editing ? 'Update Meeting' : 'Create Meeting'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:text-gray-700 border hover:border-gray-300 transition">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Meetings List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No AGM meetings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m, idx) => (
            <div key={m.id} className="bg-white rounded-xl shadow-sm border p-4 flex items-start gap-4">
              <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">{idx + 1}</span>
              {m.cover_image && (
                <img src={m.cover_image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-800 truncate">{m.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[m.status] || ''}`}>
                    {m.status_display || m.status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                  <span>Year: {m.year}</span>
                  <span>Date: {m.date}</span>
                  {m.venue && <span>📍 {m.venue}</span>}
                  <span>{m.registered_count || 0} registered</span>
                </div>
                {m.agenda && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{m.agenda}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {canEdit(PERMISSIONS.EDIT_AGM) && (
                <button onClick={() => handleEdit(m)}
                  className="p-2 text-gray-400 hover:text-[#0B5D1E] transition" title="Edit">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                )}
                {canDelete(PERMISSIONS.DELETE_AGM) && (
                <button onClick={() => handleDelete(m.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition" title="Delete">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ AGM ATTENDANCES TAB ═══
// ══════════════════════════════════════════════════════════════

function AGMAttendancesTab() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [meetings, setMeetings] = useState([]);
  const [selectedAGM, setSelectedAGM] = useState('');
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkRegistering, setBulkRegistering] = useState(false);
  const [showSingleForm, setShowSingleForm] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [singleRegistering, setSingleRegistering] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [regPayment, setRegPayment] = useState({ payment_date: '', amount_paid: '', receipt: null });

  useEffect(() => {
    api.get('/admin/agm/')
      .then(r => setMeetings(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const fetchAttendances = useCallback(() => {
    if (!selectedAGM) { setAttendances([]); return; }
    setLoading(true);
    let url = `/admin/agm/${selectedAGM}/attendances/?`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setAttendances(r.data.results || r.data))
      .catch(() => toast.error('Failed to load attendances'))
      .finally(() => setLoading(false));
  }, [selectedAGM, search]);

  useEffect(() => { fetchAttendances(); }, [fetchAttendances]);

  // Search members for single registration
  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
        .then(r => setMemberResults(r.data.results || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleAction = async (attId, action) => {
    try {
      const res = await api.post(`/admin/agm-attendances/${attId}/action/`, { action });
      setAttendances(prev => prev.map(a => a.id === attId ? res.data : a));
      const msgs = { mark_attendance: 'Attendance marked', unmark_attendance: 'Attendance unmarked', approve: 'Attendance approved', reject: 'Attendance rejected', issue_certificate: 'Certificate issued' };
      toast.success(msgs[action] || 'Action completed');
    } catch {
      toast.error('Action failed');
    }
  };

  const handleDeleteAttendance = async (attId) => {
    if (!window.confirm('Delete this attendance record? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/agm-attendances/${attId}/delete/`);
      setAttendances(prev => prev.filter(a => a.id !== attId));
      toast.success('Attendance deleted');
    } catch {
      toast.error('Failed to delete attendance');
    }
  };

  const handleBulkRegister = async () => {
    if (!selectedAGM) return;
    if (!window.confirm('Register all active members for this AGM?')) return;
    setBulkRegistering(true);
    try {
      const res = await api.post(`/admin/agm/${selectedAGM}/bulk-register/`, {});
      toast.success(res.data.detail);
      fetchAttendances();
    } catch {
      toast.error('Bulk registration failed');
    }
    setBulkRegistering(false);
  };

  const handleSingleRegister = async () => {
    if (!selectedAGM || !selectedMember) return;
    setSingleRegistering(true);
    try {
      const formData = new FormData();
      formData.append('member_id', selectedMember.id);
      if (regPayment.payment_date) formData.append('payment_date', regPayment.payment_date);
      if (regPayment.amount_paid) formData.append('amount_paid', regPayment.amount_paid);
      if (regPayment.receipt) formData.append('receipt', regPayment.receipt);
      await api.post(`/admin/agm/${selectedAGM}/single-register/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${selectedMember.full_name || selectedMember.user_full_name} registered successfully`);
      setSelectedMember(null);
      setMemberSearch('');
      setShowSingleForm(false);
      setRegPayment({ payment_date: '', amount_paid: '', receipt: null });
      fetchAttendances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setSingleRegistering(false);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <select value={selectedAGM} onChange={e => setSelectedAGM(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">-- Select AGM Meeting --</option>
          {meetings.map(m => (
            <option key={m.id} value={m.id}>{m.title} — {m.year} ({m.status})</option>
          ))}
        </select>
        <input type="text" placeholder="Search attendees..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        {selectedAGM && canAdd(PERMISSIONS.ADD_AGM) && (
          <div className="flex gap-2">
            <button onClick={() => setShowSingleForm(!showSingleForm)}
              className="bg-[#0B5D1E] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition whitespace-nowrap">
              {showSingleForm ? 'Cancel' : '+ Register Member'}
            </button>
            <button onClick={handleBulkRegister} disabled={bulkRegistering}
              className="bg-[#C8A951] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#b89740] transition whitespace-nowrap disabled:opacity-50">
              {bulkRegistering ? 'Registering...' : 'Bulk Register All'}
            </button>
          </div>
        )}
      </div>

      {/* Single Registration Form */}
      {showSingleForm && selectedAGM && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-4">Register Single Member</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search Member</label>
              <input type="text" value={memberSearch} onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                placeholder="Type name or membership number..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              {memberResults.length > 0 && !selectedMember && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {memberResults.map(m => (
                    <button key={m.id} type="button"
                      onClick={() => {
                        setSelectedMember(m);
                        setMemberSearch(`${m.full_name || m.user_full_name} (${m.membership_number || ''})`);
                        setMemberResults([]);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">
                      <span className="font-medium">{m.full_name || m.user_full_name}</span>
                      <span className="text-gray-400 ml-2">{m.membership_number}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Date of Payment</label>
              <input type="date" value={regPayment.payment_date} onChange={e => setRegPayment({ ...regPayment, payment_date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Amount Paid (₦)</label>
              <input type="number" step="0.01" min="0" value={regPayment.amount_paid} onChange={e => setRegPayment({ ...regPayment, amount_paid: e.target.value })}
                placeholder="Enter amount" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Upload Receipt</label>
              <input type="file" accept="image/*,.pdf" onChange={e => {
                const file = e.target.files[0] || null;
                if (file && file.size > 100 * 1024) {
                  toast.error('Receipt file must be less than 100KB');
                  e.target.value = '';
                  return;
                }
                setRegPayment({ ...regPayment, receipt: file });
              }}
                className="w-full border rounded-lg px-3 py-2 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-[#0B5D1E]/10 file:text-[#0B5D1E] file:text-sm file:font-semibold" />
              <p className="text-xs text-gray-400 mt-1">Max file size: 100KB</p>
            </div>
          </div>
          {selectedMember && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm text-green-800">
              Selected: <strong>{selectedMember.full_name || selectedMember.user_full_name}</strong> — {selectedMember.membership_number}
            </div>
          )}
          <div className="mt-4 flex gap-3">
            <button onClick={handleSingleRegister}
              disabled={!selectedMember || singleRegistering}
              className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50">
              {singleRegistering ? 'Registering...' : 'Register Member'}
            </button>
            <button onClick={() => { setShowSingleForm(false); setRegPayment({ payment_date: '', amount_paid: '', receipt: null }); }}
              className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </div>
      )}

      {!selectedAGM ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">Select an AGM meeting to view attendances</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : attendances.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No attendances found for this AGM</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">S/N</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Membership #</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Payment Date</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Amount Paid</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Receipt</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Attendance</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Certificate</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {attendances.map((a, idx) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {a.member_name}
                    {a.registered_by === 'admin' && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded">Admin</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{a.membership_number}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      a.status === 'approved' ? 'bg-green-100 text-green-700' :
                      a.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {a.status ? a.status.charAt(0).toUpperCase() + a.status.slice(1) : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{a.payment_date || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{a.amount_paid ? `₦${Number(a.amount_paid).toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {a.receipt ? (
                      <button onClick={() => setReceiptPreview(a.receipt)} className="text-blue-600 hover:underline text-xs font-semibold">View</button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {a.attendance_marked ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Marked
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Not marked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {a.certificate_issued ? (
                      <span className="text-green-600 text-xs font-semibold">Issued</span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {canEdit(PERMISSIONS.EDIT_AGM) && a.status !== 'approved' && (
                        <button onClick={() => handleAction(a.id, 'approve')}
                          className="text-xs px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-semibold transition">
                          Approve
                        </button>
                      )}
                      {canEdit(PERMISSIONS.EDIT_AGM) && a.status !== 'rejected' && (
                        <button onClick={() => handleAction(a.id, 'reject')}
                          className="text-xs px-3 py-1 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 font-semibold transition">
                          Reject
                        </button>
                      )}
                      {canEdit(PERMISSIONS.EDIT_AGM) && (!a.attendance_marked ? (
                        <button onClick={() => handleAction(a.id, 'mark_attendance')}
                          className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-semibold transition">
                          Mark Present
                        </button>
                      ) : (
                        <button onClick={() => handleAction(a.id, 'unmark_attendance')}
                          className="text-xs px-3 py-1 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 font-semibold transition">
                          Unmark
                        </button>
                      ))}
                      {canDelete(PERMISSIONS.DELETE_AGM) && (
                        <button onClick={() => handleDeleteAttendance(a.id)}
                          className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-semibold transition">
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {receiptPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setReceiptPreview(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl max-h-[90vh] mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-800">Payment Receipt</h3>
              <button onClick={() => setReceiptPreview(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4 flex items-center justify-center" style={{ maxHeight: 'calc(90vh - 56px)', overflow: 'auto' }}>
              {receiptPreview.match(/\.pdf$/i) ? (
                <iframe src={receiptPreview} title="Receipt" className="w-full" style={{ height: '70vh' }} />
              ) : (
                <img src={receiptPreview} alt="Receipt" className="max-w-full max-h-[70vh] object-contain" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ AGM MATERIALS TAB ═══
// ══════════════════════════════════════════════════════════════

function AGMMaterialsTab() {
  const { canEdit } = usePermissions();
  const [meetings, setMeetings] = useState([]);
  const [selectedAGM, setSelectedAGM] = useState('');
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/admin/agm/')
      .then(r => setMeetings(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const fetchAttendances = useCallback(() => {
    if (!selectedAGM) { setAttendances([]); return; }
    setLoading(true);
    let url = `/admin/agm/${selectedAGM}/attendances/?`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setAttendances(r.data.results || r.data))
      .catch(() => toast.error('Failed to load attendances'))
      .finally(() => setLoading(false));
  }, [selectedAGM, search]);

  useEffect(() => { fetchAttendances(); }, [fetchAttendances]);

  const handleToggleMaterials = async (attId, currentVal) => {
    const action = currentVal ? 'uncollect_materials' : 'collect_materials';
    try {
      const res = await api.post(`/admin/agm-attendances/${attId}/action/`, { action });
      setAttendances(prev => prev.map(a => a.id === attId ? res.data : a));
      toast.success(currentVal ? 'Marked as not collected' : 'Marked as collected');
    } catch {
      toast.error('Action failed');
    }
  };

  const collectedCount = attendances.filter(a => a.materials_collected).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <select value={selectedAGM} onChange={e => setSelectedAGM(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">-- Select AGM Meeting --</option>
          {meetings.map(m => (
            <option key={m.id} value={m.id}>{m.title} — {m.year} ({m.status})</option>
          ))}
        </select>
        <input type="text" placeholder="Search members..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
      </div>

      {selectedAGM && attendances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Total Registered</p>
            <p className="text-2xl font-bold text-gray-900">{attendances.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Collected</p>
            <p className="text-2xl font-bold text-green-600">{collectedCount}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase">Not Collected</p>
            <p className="text-2xl font-bold text-red-600">{attendances.length - collectedCount}</p>
          </div>
        </div>
      )}

      {!selectedAGM ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">Select an AGM meeting to view materials collection</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : attendances.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No registered members for this AGM</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">S/N</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Membership #</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Bag / Materials</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {attendances.map((a, idx) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{a.member_name}</td>
                  <td className="px-4 py-3 text-gray-500">{a.membership_number}</td>
                  <td className="px-4 py-3 text-center">
                    {a.materials_collected ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Collected
                      </span>
                    ) : (
                      <span className="text-red-500 text-xs font-semibold">Not Collected</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(PERMISSIONS.EDIT_AGM) && (!a.materials_collected ? (
                      <button onClick={() => handleToggleMaterials(a.id, false)}
                        className="text-xs px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-semibold transition">
                        Mark Collected
                      </button>
                    ) : (
                      <button onClick={() => handleToggleMaterials(a.id, true)}
                        className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-semibold transition">
                        Undo
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ HOTEL REGISTRATION TAB ═══
// ══════════════════════════════════════════════════════════════

const ROOM_TYPE_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'double', label: 'Double' },
  { value: 'suite', label: 'Suite' },
];

function HotelRegistrationTab() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [meetings, setMeetings] = useState([]);
  const [selectedAGM, setSelectedAGM] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [form, setForm] = useState({
    hotel_name: '', room_type: 'single', check_in: '', check_out: '', nights: '1', special_requests: '', confirmed: false,
  });

  useEffect(() => {
    api.get('/admin/agm/')
      .then(r => setMeetings(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const fetchRegistrations = useCallback(() => {
    if (!selectedAGM) { setRegistrations([]); return; }
    setLoading(true);
    let url = `/admin/agm/${selectedAGM}/hotel-registrations/?`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setRegistrations(r.data.results || r.data))
      .catch(() => toast.error('Failed to load hotel registrations'))
      .finally(() => setLoading(false));
  }, [selectedAGM, search]);

  useEffect(() => { fetchRegistrations(); }, [fetchRegistrations]);

  // Search members
  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
        .then(r => setMemberResults(r.data.results || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  // Auto-calculate nights
  useEffect(() => {
    if (form.check_in && form.check_out) {
      const d1 = new Date(form.check_in);
      const d2 = new Date(form.check_out);
      const diff = Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
      setForm(prev => ({ ...prev, nights: diff.toString() }));
    }
  }, [form.check_in, form.check_out]);

  const resetForm = () => {
    setForm({ hotel_name: '', room_type: 'single', check_in: '', check_out: '', nights: '1', special_requests: '', confirmed: false });
    setSelectedMember(null);
    setMemberSearch('');
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (reg) => {
    setForm({
      hotel_name: reg.hotel_name, room_type: reg.room_type,
      check_in: reg.check_in, check_out: reg.check_out,
      nights: reg.nights?.toString() || '1', special_requests: reg.special_requests || '',
      confirmed: reg.confirmed,
    });
    setEditing(reg);
    setSelectedMember({ id: reg.member, full_name: reg.member_name, membership_number: reg.membership_number });
    setMemberSearch(`${reg.member_name} (${reg.membership_number})`);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAGM) { toast.error('Select an AGM first'); return; }

    const payload = {
      ...form,
      agm: selectedAGM,
      nights: parseInt(form.nights) || 1,
    };

    if (!editing && selectedMember) {
      payload.member = selectedMember.id;
    }

    try {
      if (editing) {
        await api.patch(`/admin/agm-hotel-registrations/${editing.id}/`, payload);
        toast.success('Hotel registration updated');
      } else {
        if (!selectedMember) { toast.error('Select a member'); return; }
        await api.post(`/admin/agm/${selectedAGM}/hotel-registrations/`, payload);
        toast.success('Hotel registration created');
      }
      resetForm();
      fetchRegistrations();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Failed to save';
      toast.error(detail);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this hotel registration?')) return;
    try {
      await api.delete(`/admin/agm-hotel-registrations/${id}/`);
      toast.success('Hotel registration deleted');
      fetchRegistrations();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleConfirm = async (reg) => {
    try {
      const res = await api.patch(`/admin/agm-hotel-registrations/${reg.id}/`, { confirmed: !reg.confirmed });
      setRegistrations(prev => prev.map(r => r.id === reg.id ? res.data : r));
      toast.success(reg.confirmed ? 'Unconfirmed' : 'Confirmed');
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <select value={selectedAGM} onChange={e => setSelectedAGM(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
          <option value="">-- Select AGM Meeting --</option>
          {meetings.map(m => (
            <option key={m.id} value={m.id}>{m.title} — {m.year} ({m.status})</option>
          ))}
        </select>
        <input type="text" placeholder="Search registrations..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
        {selectedAGM && canAdd(PERMISSIONS.ADD_AGM) && (
          <button onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition whitespace-nowrap">
            {showForm ? 'Cancel' : '+ New Registration'}
          </button>
        )}
      </div>

      {/* Hotel Registration Form */}
      {showForm && selectedAGM && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-4">{editing ? 'Edit Hotel Registration' : 'New Hotel Registration'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!editing && (
              <div className="relative md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Member</label>
                <input type="text" value={memberSearch} onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                  placeholder="Search member by name or number..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                {memberResults.length > 0 && !selectedMember && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                    {memberResults.map(m => (
                      <button key={m.id} type="button"
                        onClick={() => {
                          setSelectedMember(m);
                          setMemberSearch(`${m.full_name || m.user_full_name} (${m.membership_number || ''})`);
                          setMemberResults([]);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">
                        <span className="font-medium">{m.full_name || m.user_full_name}</span>
                        <span className="text-gray-400 ml-2">{m.membership_number}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedMember && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {selectedMember.full_name || selectedMember.user_full_name} — {selectedMember.membership_number}
                  </p>
                )}
              </div>
            )}
            {editing && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Member</label>
                <input type="text" disabled value={memberSearch}
                  className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-sm text-gray-500" />
              </div>
            )}
            <FormInput label="Hotel Name" required value={form.hotel_name} onChange={e => setForm({...form, hotel_name: e.target.value})} />
            <FormSelect label="Room Type" options={ROOM_TYPE_OPTIONS} value={form.room_type} onChange={e => setForm({...form, room_type: e.target.value})} />
            <FormInput label="Check-in" type="date" required value={form.check_in} onChange={e => setForm({...form, check_in: e.target.value})} />
            <FormInput label="Check-out" type="date" required value={form.check_out} onChange={e => setForm({...form, check_out: e.target.value})} />
            <FormInput label="Nights" type="number" min="1" value={form.nights} onChange={e => setForm({...form, nights: e.target.value})} />
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.confirmed} onChange={e => setForm({...form, confirmed: e.target.checked})}
                  className="rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                <span className="text-sm font-medium text-gray-600">Confirmed</span>
              </label>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">Special Requests</label>
              <textarea rows={2} value={form.special_requests} onChange={e => setForm({...form, special_requests: e.target.value})}
                placeholder="Any special requests or notes..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button type="submit" className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
              {editing ? 'Update Registration' : 'Create Registration'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:text-gray-700 border hover:border-gray-300 transition">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Registrations List */}
      {!selectedAGM ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">Select an AGM meeting to view hotel registrations</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : registrations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-400">No hotel registrations for this AGM</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">S/N</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Member</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Hotel</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Room</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Check-in</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Check-out</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Nights</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {registrations.map((r, idx) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{r.member_name}</p>
                    <p className="text-xs text-gray-400">{r.membership_number}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.hotel_name}</td>
                  <td className="px-4 py-3 text-gray-500">{r.room_type_display || r.room_type}</td>
                  <td className="px-4 py-3 text-gray-500">{r.check_in}</td>
                  <td className="px-4 py-3 text-gray-500">{r.check_out}</td>
                  <td className="px-4 py-3 text-center text-gray-700 font-medium">{r.nights}</td>
                  <td className="px-4 py-3 text-center">
                    {canEdit(PERMISSIONS.EDIT_AGM) ? (
                    <button onClick={() => handleToggleConfirm(r)}
                      className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
                        r.confirmed
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      }`}>
                      {r.confirmed ? 'Confirmed' : 'Pending'}
                    </button>
                    ) : (
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      r.confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {r.confirmed ? 'Confirmed' : 'Pending'}
                    </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canEdit(PERMISSIONS.EDIT_AGM) && (
                      <button onClick={() => handleEdit(r)}
                        className="p-1.5 text-gray-400 hover:text-[#0B5D1E] transition" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      )}
                      {canDelete(PERMISSIONS.DELETE_AGM) && (
                      <button onClick={() => handleDelete(r.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Delete">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
